import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Clock, Loader2, MessageSquareText, Phone } from "lucide-react";
import { AppNotice } from "../../../components/common/AppNotice.js";
import { Badge } from "../../../components/ui/badge/index.js";
import { Button } from "../../../components/ui/button/index.js";
import { cn } from "../../../lib/utils.js";
import { TechnicalValue } from "../TechnicalValue.js";
import { OwnershipOtpInput } from "./OwnershipOtpInput.js";
import { formatVerificationDeadline } from "./add-phone-errors.js";
import {
  formatVerificationCountdown,
  isLongVerificationCooldown,
} from "./verification-countdown.js";

export type VerificationCodeMethod = "SMS" | "VOICE";

/** First-strong isolate: a digits-only clock stays LTR, a Hebrew "2 שע׳ 15 דק׳" stays RTL. */
const FIRST_STRONG_ISOLATE_START = "\u2068";
const ISOLATE_END = "\u2069";

const secondaryActionClass =
  "h-auto px-0 py-1 text-[0.8125rem] font-medium no-underline not-disabled:hover:underline disabled:cursor-not-allowed disabled:text-ink-subtle disabled:no-underline disabled:opacity-100";

export function VerificationCooldownNotice({
  id,
  remainingMs,
  retryAfter,
  retryAfterSource,
}: {
  id: string;
  remainingMs: number;
  retryAfter: string;
  retryAfterSource: "application" | "provider" | null;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const remaining = `${FIRST_STRONG_ISOLATE_START}${formatVerificationCountdown(t, remainingMs)}${ISOLATE_END}`;
  const showDeadline = retryAfterSource === "provider" || isLongVerificationCooldown(remainingMs);
  return (
    <div
      id={id}
      role="timer"
      data-testid="add-phone-cooldown"
      className="flex items-start justify-center gap-1.5 text-center text-[0.8125rem] leading-snug text-ink-muted"
    >
      <Clock className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
      <p className="m-0 tabular-nums">
        {retryAfterSource === "provider"
          ? t("whatsapp.addPhone.cooldown.provider", { remaining })
          : t("whatsapp.addPhone.cooldown.application", { remaining })}
        {showDeadline ? (
          <>
            {" "}
            {t("whatsapp.addPhone.cooldown.availableAt", {
              time: formatVerificationDeadline(retryAfter, i18n.language),
            })}
          </>
        ) : null}
      </p>
    </div>
  );
}

export function VerificationPhoneSummary({
  displayPhoneNumber,
}: {
  displayPhoneNumber: string | null;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  if (!displayPhoneNumber) return null;
  return (
    <div className="grid gap-0.5 rounded-lg border border-line bg-surface-muted/60 px-4 py-3 text-start">
      <span className="text-[0.75rem] text-ink-muted">
        {t("whatsapp.addPhone.verification.phoneLabel")}
      </span>
      <TechnicalValue className="w-fit text-[1.05rem] font-semibold tabular-nums text-ink">
        {displayPhoneNumber}
      </TechnicalValue>
    </div>
  );
}

function MethodIcon({
  method,
  className,
}: {
  method: VerificationCodeMethod;
  className?: string;
}): React.JSX.Element {
  return method === "VOICE" ? (
    <Phone className={className} aria-hidden="true" />
  ) : (
    <MessageSquareText className={className} aria-hidden="true" />
  );
}

export function VerificationMethodStep({
  displayPhoneNumber,
  codeMethod,
  onMethodChange,
  methodsDisabled,
  deliveryUnconfirmed,
  returnToCodeAvailable,
  onAlreadyReceivedCode,
  alreadyReceivedDisabled,
  cooldown,
  notice,
}: {
  displayPhoneNumber: string | null;
  codeMethod: VerificationCodeMethod;
  onMethodChange: (method: VerificationCodeMethod) => void;
  /** Busy or inside the request_code cooldown. */
  methodsDisabled: boolean;
  deliveryUnconfirmed: boolean;
  /** Arrived via Change Method after a sent or unconfirmed code. Never calls request_code. */
  returnToCodeAvailable: boolean;
  onAlreadyReceivedCode: () => void;
  alreadyReceivedDisabled: boolean;
  cooldown: ReactNode;
  notice: ReactNode;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="grid gap-4" data-testid="add-phone-method-step">
      <p className="m-0 text-[0.875rem] leading-relaxed text-ink-muted">
        {t("whatsapp.addPhone.steps.methodDescription")}
      </p>
      <VerificationPhoneSummary displayPhoneNumber={displayPhoneNumber} />
      <fieldset className="m-0 grid gap-2 border-0 p-0" disabled={methodsDisabled}>
        <legend className="mb-2 p-0 text-start text-[0.875rem] font-semibold text-ink">
          {t("whatsapp.addPhone.methods.question")}
        </legend>
        {(["SMS", "VOICE"] as const).map((method) => {
          const selected = codeMethod === method;
          return (
            <label
              key={method}
              data-method={method}
              data-selected={selected || undefined}
              className={cn(
                "flex cursor-pointer items-center gap-3 rounded-lg border bg-surface px-3.5 py-3 text-start transition-[border-color,background-color,box-shadow] duration-150 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-accent",
                selected
                  ? "border-whatsapp shadow-[0_0_0_1px_var(--color-whatsapp)]"
                  : "border-line hover:border-line-strong",
                methodsDisabled &&
                  "cursor-not-allowed border-line bg-surface-muted/60 shadow-none hover:border-line",
              )}
            >
              <input
                type="radio"
                className="sr-only"
                name="add-phone-code-method"
                value={method}
                checked={selected}
                disabled={methodsDisabled}
                onChange={() => onMethodChange(method)}
              />
              <span
                className={cn(
                  "inline-grid size-9 shrink-0 place-items-center rounded-md",
                  selected && !methodsDisabled
                    ? "bg-whatsapp-muted text-success"
                    : "bg-surface-muted text-ink-muted",
                )}
              >
                <MethodIcon method={method} className="size-4.5" />
              </span>
              <span className="grid min-w-0 flex-1 gap-0.5">
                <span
                  className={cn(
                    "text-[0.875rem] font-semibold",
                    methodsDisabled ? "text-ink-muted" : "text-ink",
                  )}
                >
                  {method === "SMS"
                    ? t("whatsapp.addPhone.methods.sms")
                    : t("whatsapp.addPhone.methods.voice")}
                </span>
                <span className="text-[0.75rem] leading-snug text-ink-muted">
                  {method === "SMS"
                    ? t("whatsapp.addPhone.methods.smsDescription")
                    : t("whatsapp.addPhone.methods.voiceDescription")}
                </span>
              </span>
              <span
                className={cn(
                  "flex size-4 shrink-0 items-center justify-center rounded-full border",
                  selected && !methodsDisabled ? "border-whatsapp" : "border-line-strong",
                )}
                aria-hidden="true"
              >
                {selected ? (
                  <span
                    className={cn(
                      "size-2 rounded-full",
                      methodsDisabled ? "bg-ink-subtle" : "bg-whatsapp",
                    )}
                  />
                ) : null}
              </span>
            </label>
          );
        })}
      </fieldset>
      {deliveryUnconfirmed || returnToCodeAvailable ? (
        <div
          className="grid gap-2"
          data-testid={deliveryUnconfirmed ? "add-phone-delivery-unconfirmed" : "add-phone-return-to-code"}
        >
          {deliveryUnconfirmed ? (
            <AppNotice
              severity="warning"
              description={t("whatsapp.addPhone.verification.deliveryUnconfirmed")}
            />
          ) : null}
          <Button
            variant="outline"
            type="button"
            disabled={alreadyReceivedDisabled}
            onClick={onAlreadyReceivedCode}
            data-testid="add-phone-already-received"
          >
            {t("whatsapp.addPhone.actions.alreadyReceivedCode")}
          </Button>
        </div>
      ) : null}
      {notice}
      {cooldown}
    </div>
  );
}

export function VerificationCodeStep({
  codeMethod,
  codeSent,
  deliveryUnconfirmed,
  displayPhoneNumber,
  verificationCode,
  onCodeChange,
  codeInvalid,
  busy,
  verifyDisabled,
  onVerify,
  resendDisabled,
  onResend,
  changeMethodDisabled,
  onChangeMethod,
  cooldown,
  cooldownId,
  notice,
}: {
  codeMethod: VerificationCodeMethod;
  codeSent: boolean;
  deliveryUnconfirmed: boolean;
  displayPhoneNumber: string | null;
  verificationCode: string;
  onCodeChange: (code: string) => void;
  codeInvalid: boolean;
  busy: boolean;
  verifyDisabled: boolean;
  onVerify: () => void;
  resendDisabled: boolean;
  onResend: () => void;
  changeMethodDisabled: boolean;
  onChangeMethod: () => void;
  cooldown: ReactNode;
  cooldownId: string | undefined;
  notice: ReactNode;
}): React.JSX.Element {
  const { t } = useTranslation();
  const description = codeSent
    ? codeMethod === "VOICE"
      ? t("whatsapp.addPhone.steps.codeDescriptionVoice")
      : t("whatsapp.addPhone.steps.codeDescriptionSms")
    : t("whatsapp.addPhone.steps.codeDescriptionUnconfirmed");
  return (
    <div className="grid justify-items-center gap-4 text-center" data-testid="add-phone-code-step">
      <div className="grid justify-items-center gap-1.5">
        {codeSent ? (
          <Badge variant="whatsapp" data-testid="add-phone-code-sent">
            <MethodIcon method={codeMethod} className="size-3.5" />
            {codeMethod === "VOICE"
              ? t("whatsapp.addPhone.verification.sentVoice")
              : t("whatsapp.addPhone.verification.sentSms")}
          </Badge>
        ) : null}
        <p className="m-0 text-[0.875rem] leading-relaxed text-ink-muted">{description}</p>
        {displayPhoneNumber ? (
          <TechnicalValue className="text-[1.15rem] font-semibold tabular-nums text-ink">
            {displayPhoneNumber}
          </TechnicalValue>
        ) : null}
      </div>
      {!codeSent && deliveryUnconfirmed ? (
        <AppNotice
          severity="warning"
          description={t("whatsapp.addPhone.verification.deliveryUnconfirmed")}
          testId="add-phone-delivery-unconfirmed"
        />
      ) : null}
      <span id="add-phone-verification-code-label" className="sr-only">
        {t("whatsapp.addPhone.fields.verificationCode")}
      </span>
      <OwnershipOtpInput
        labelId="add-phone-verification-code-label"
        value={verificationCode}
        disabled={busy}
        invalid={codeInvalid}
        onChange={onCodeChange}
      />
      {notice}
      <Button
        type="button"
        variant="default"
        className="w-full"
        disabled={verifyDisabled}
        aria-busy={busy || undefined}
        onClick={onVerify}
        data-testid="add-phone-verify"
      >
        {busy ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : null}
        {busy ? t("whatsapp.addPhone.actions.verifying") : t("whatsapp.addPhone.actions.verify")}
      </Button>
      {cooldown}
      <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1">
        <Button
          variant="link"
          type="button"
          className={secondaryActionClass}
          disabled={resendDisabled}
          aria-describedby={resendDisabled ? cooldownId : undefined}
          onClick={onResend}
          data-testid="add-phone-resend"
        >
          {codeMethod === "VOICE"
            ? t("whatsapp.addPhone.resend.voice")
            : t("whatsapp.addPhone.resend.sms")}
        </Button>
        <Button
          variant="link"
          type="button"
          className={secondaryActionClass}
          disabled={changeMethodDisabled}
          aria-describedby={changeMethodDisabled ? cooldownId : undefined}
          onClick={onChangeMethod}
          data-testid="add-phone-change-method"
        >
          {t("whatsapp.addPhone.actions.changeMethod")}
        </Button>
      </div>
    </div>
  );
}
